import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { MODELS } from "../data/models";
import { useContent } from "../lib/ContentContext";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";

const priorityModels = MODELS.filter((m) => m.featured).sort((a, b) => a.priority - b.priority);
const otherModels = MODELS.filter((m) => !m.featured);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useContent();
  const { settings } = useSettings();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // High-conversion default message (admin can override via /admin/content → nav.cta_message)
  const ctaMessage = t(
    "nav.cta_message",
    `Halo Kak ${settings.sales_name || "Kiel"}, saya tertarik konsultasi kredit BYD. Mohon dibantu simulasi DP minimal, cicilan paling ringan, dan promo terbaru yang sedang berjalan. Apakah masih ada slot test drive minggu ini? Terima kasih 🙏`
  ).replace("{name}", settings.sales_name || "Kiel");
  const ctaLabel = settings.wa_cta_hero || t("nav.cta_button", "Konsultasi Kredit");
  const waHref = buildWaLink(settings, ctaMessage);

  return (
    <header
      className="glass-nav sticky top-0 z-40 w-full"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <span className="text-xl md:text-2xl font-medium tracking-tight text-[#0F0F0F]">
              BYD
            </span>
            <span className="hidden sm:inline text-xs uppercase tracking-[0.2em] text-[#6E6E73] border-l border-[#E5E5EA] pl-2 ml-1">
              Cibubur
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#0F0F0F]" : "text-[#6E6E73] hover:text-[#0F0F0F]"
                }`
              }
              data-testid="nav-home"
            >
              Beranda
            </NavLink>
            {priorityModels.map((m) => (
              <NavLink
                key={m.slug}
                to={`/model/${m.slug}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors relative ${
                    isActive ? "text-[#0F0F0F]" : "text-[#6E6E73] hover:text-[#0F0F0F]"
                  }`
                }
                data-testid={`nav-model-${m.slug}`}
              >
                <span className="inline-flex items-center gap-2">
                  {m.name}
                  <span className="text-[9px] font-semibold bg-[#0F0F0F] text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider leading-none">
                    Hot
                  </span>
                </span>
              </NavLink>
            ))}
            <NavLink
              to="/models"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#0F0F0F]" : "text-[#6E6E73] hover:text-[#0F0F0F]"
                }`
              }
              data-testid="nav-all-models"
            >
              Semua Model
            </NavLink>
            <NavLink
              to="/blog"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#0F0F0F]" : "text-[#6E6E73] hover:text-[#0F0F0F]"
                }`
              }
              data-testid="nav-blog"
            >
              Blog
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppLead("navbar-cta")}
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-5 py-2.5 text-sm font-medium transition-colors"
              data-testid="navbar-cta"
            >
              <MessageCircle className="w-4 h-4" />
              {ctaLabel}
            </a>
            <button
              className="lg:hidden p-2 -mr-2 text-[#0F0F0F]"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="navbar-menu-toggle"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-[#E5E5EA] bg-white"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 space-y-5">
            <Link to="/" className="block text-base font-medium">
              {t("nav.home", "Beranda")}
            </Link>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-widest text-[#86868B]">
                {t("nav.mobile_featured_label", "Unit Terlaris")}
              </p>
              {priorityModels.map((m) => (
                <Link
                  key={m.slug}
                  to={`/model/${m.slug}`}
                  className="block text-base font-medium"
                  data-testid={`mobile-nav-${m.slug}`}
                >
                  {m.name}
                  <span className="ml-2 text-[10px] font-semibold bg-[#0F0F0F] text-white px-1.5 py-0.5 rounded-full uppercase">
                    {t("nav.featured_badge", "Hot")}
                  </span>
                </Link>
              ))}
            </div>
            <div className="space-y-3 border-t border-[#E5E5EA] pt-4">
              <p className="text-xs uppercase tracking-widest text-[#86868B]">
                {t("nav.mobile_others_label", "Model Lainnya")}
              </p>
              {otherModels.map((m) => (
                <Link
                  key={m.slug}
                  to={`/model/${m.slug}`}
                  className="block text-sm text-[#6E6E73]"
                >
                  {m.name}
                </Link>
              ))}
              <Link to="/blog" className="block text-sm text-[#6E6E73]" data-testid="mobile-nav-blog">
                {t("nav.blog", "Blog")}
              </Link>
            </div>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppLead("navbar-cta-mobile")}
              className="inline-flex w-full justify-center items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-5 py-3 text-sm font-medium transition-colors"
              data-testid="mobile-navbar-cta"
            >
              <MessageCircle className="w-4 h-4" />
              {ctaLabel}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
